import { useState, useEffect, useCallback, useMemo } from 'react';
import { useFirstAvailableQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import { useService } from './useBookingComputed';

export function useStartingMonth() {
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const startingMonthMode = useBookingStore(
		(state) => state.startingMonthMode
	);
	const startingMonth = useBookingStore((state) => state.startingMonth);
	const startingYear = useBookingStore((state) => state.startingYear);

	const { data: service, isLoading: serviceLoading } = useService();
	const serviceLoaded = !serviceLoading && !!service;
	// Get filtered date if available (for testing). Use gpb_current_date.
	const { filteredDate } = useMemo(() => {
		if (!window.gform?.applyFilters) {
			return { filteredDate: null };
		}

		// Deprecated: use gpb_current_date.
		let filtered = window.gform.applyFilters(
			'gp_bookings_current_date',
			new Date()
		);
		filtered = window.gform.applyFilters( 'gpb_current_date', filtered );
		const actual = new Date();
		const isFiltered = filtered.getTime() !== actual.getTime();

		return {
			filteredDate: isFiltered ? filtered : null,
		};
	}, []);

	// Initialize current month based on mode
	const initialMonth = useMemo(() => {
		if (filteredDate && startingMonthMode !== 'specific') {
			// If date is filtered (for testing), use the filtered date
			return new Date(
				filteredDate.getFullYear(),
				filteredDate.getMonth(),
				1
			);
		}

		switch (startingMonthMode) {
			case 'current':
				return new Date();
			case 'specific':
				if (startingMonth && startingYear) {
					return new Date(startingYear, startingMonth - 1, 1);
				}
				return new Date();
			case 'first_availability_window':
			default:
				// Will be updated when query completes
				return new Date();
		}
	}, [startingMonthMode, startingMonth, startingYear, filteredDate]);

	const [currentMonth, setCurrentMonth] = useState(initialMonth);

	// Fetch first available month using React Query
	const {
		data: firstAvailableData,
		isLoading,
		isFetching,
	} = useFirstAvailableQuery(
		{
			serviceId: currentServiceId,
			resources:
				selectedResourceIds.length > 0
					? selectedResourceIds
					: undefined,
		},
		{
			enabled:
				serviceLoaded &&
				startingMonthMode === 'first_availability_window',
			staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		}
	);

	// Update current month when first available data is fetched
	useEffect(() => {
		if (
			firstAvailableData &&
			startingMonthMode === 'first_availability_window'
		) {
			const firstAvailableDate = new Date(
				firstAvailableData.year,
				firstAvailableData.month - 1,
				1
			);
			setCurrentMonth(firstAvailableDate);
		}
	}, [firstAvailableData, startingMonthMode]);

	const onMonthChange = useCallback((month: Date) => {
		setCurrentMonth(month);
	}, []);

	return {
		currentMonth,
		onMonthChange,
		loading: isLoading || isFetching,
	};
}
